import { Deferred, router, usePage } from "@inertiajs/react";
import { useLocalStorage } from "@uidotdev/usehooks";
import { AnimatePresence, motion } from "framer-motion";
import {
	Check,
	ChevronsUpDown,
	Columns3Icon,
	Dot,
	ListFilterIcon,
	Search,
	X,
} from "lucide-react";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "@/components/ui/command";
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import { useIsMobile } from "@/hooks/use-mobile";
import { groupLocationsByCountry } from "@/lib/locations";
import { cn, debounce, populateQueryParams } from "@/lib/utils";
import type {
	TableToolbarDataProps,
	TherapistIndexGlobalPageProps,
} from "@/pages/AdminPortal/Therapist/Index";

export interface ToolbarTableProps {
	table: TableToolbarDataProps;
}

const PREFERENCES_STORAGE_KEY = "admin-portal-therapist-preferences";

// Columns menu labels, keyed by column id; the headers read the same keys.
const COLUMN_LABEL_KEYS: Record<string, string> = {
	location: "list.columns.location",
	gender: "list.columns.gender",
	type: "list.columns.type",
	service: "list.columns.service",
	status: "list.columns.status",
	activity: "list.columns.activity",
	religion: "list.columns.religion",
	tags: "list.tags",
	createdAt: "list.joined_at",
};

// Option values only — the labels are resolved from the `therapists` namespace at
// render time, so switching language re-labels them without rebuilding these lists.
const EXIST_VALUES = ["true", "false"] as const;
const GENDER_VALUES = ["MALE", "FEMALE"] as const;
const CONTRACT_STATUS_VALUES = [
	"active",
	"expiring",
	"expired",
	"none",
] as const;
const ONLINE_STATUS_VALUES = ["online", "offline"] as const;

// A plain value/label select with a placeholder and an inline Clear action,
// used for the option-list filters (gender, contract status, exist toggles…).
function SelectFilter({
	value,
	placeholder,
	options,
	clearLabel,
	onValueChange,
}: {
	value: string;
	placeholder: string;
	options: { value: string; label: string }[];
	clearLabel: string;
	onValueChange: (value: string) => void;
}) {
	return (
		<Select value={value || ""} onValueChange={onValueChange}>
			<SelectTrigger
				className={cn(
					"shadow-inner bg-input",
					!value ? "text-muted-foreground" : "",
				)}
			>
				<SelectValue placeholder={placeholder} />
			</SelectTrigger>

			<SelectContent>
				{options.map((option) => (
					<SelectItem key={option.value} value={option.value}>
						<span>{option.label}</span>
					</SelectItem>
				))}
				{value && (
					<>
						<SelectSeparator />
						<Button
							className="w-full px-2 font-medium uppercase"
							variant="ghost"
							size="sm"
							onClick={(e) => {
								e.stopPropagation();
								onValueChange("");
							}}
						>
							{clearLabel}
						</Button>
					</>
				)}
			</SelectContent>
		</Select>
	);
}

export default function ToolbarTable({ table }: ToolbarTableProps) {
	const { url: pageURL, props: globalProps } =
		usePage<TherapistIndexGlobalPageProps>();
	const isMobile = useIsMobile();
	const { t } = useTranslation("therapists");

	// ── Show/hide filter container preference ─────────────────────────────
	const [preferences, savePreferences] = useLocalStorage(
		PREFERENCES_STORAGE_KEY,
		{ isShowFilter: false },
	);
	const handleToggleFilter = useCallback(() => {
		savePreferences((prev) => ({ ...prev, isShowFilter: !prev.isShowFilter }));
	}, [savePreferences]);

	// ── Filter options ────────────────────────────────────────────────────
	const accountStatuses = useMemo(() => {
		return [
			{
				value: "ACTIVE",
				label: t("filter.options.account_status.ACTIVE"),
				color: "text-emerald-600",
			},
			{
				value: "INACTIVE",
				label: t("filter.options.account_status.INACTIVE"),
				color: "text-destructive",
			},
		];
	}, [t]);
	const existOptions = useMemo(
		() =>
			EXIST_VALUES.map((value) => ({
				value,
				label: t(`filter.options.exist.${value}`),
			})),
		[t],
	);
	const genderOptions = useMemo(
		() =>
			GENDER_VALUES.map((value) => ({
				value,
				label: t(`filter.options.gender.${value}`),
			})),
		[t],
	);
	const contractStatusOptions = useMemo(
		() =>
			CONTRACT_STATUS_VALUES.map((value) => ({
				value,
				label: t(`filter.options.contract_status.${value}`),
			})),
		[t],
	);
	const onlineStatusOptions = useMemo(
		() =>
			ONLINE_STATUS_VALUES.map((value) => ({
				value,
				label: t(`filter.options.online_status.${value}`),
			})),
		[t],
	);
	const employmentStatuses = useMemo(
		() => globalProps?.filterOptions?.employmentStatuses || [],
		[globalProps?.filterOptions?.employmentStatuses],
	);
	const employmentTypes = useMemo(
		() => globalProps?.filterOptions?.employmentTypes || [],
		[globalProps?.filterOptions?.employmentTypes],
	);
	const services = useMemo(
		() => globalProps?.filterOptions?.services || [],
		[globalProps?.filterOptions?.services],
	);
	const locations = useMemo(
		() => globalProps?.filterOptions?.locations,
		[globalProps?.filterOptions?.locations],
	);
	const groupedLocations = useMemo(
		() => groupLocationsByCountry(globalProps?.filterOptions?.locations || []),
		[globalProps?.filterOptions?.locations],
	);

	// ── Filter state ──────────────────────────────────────────────────────
	// currentQuery is camelCased by the shared adminPortal props concern
	// (deep_transform_keys_to_camel_case), so read camelCase keys to rehydrate
	// the filters correctly after a reload.
	const currentQuery = globalProps?.adminPortal?.currentQuery;
	const [filterBy, setFilterBy] = useState({
		search: currentQuery?.search || "",
		accountStatus: currentQuery?.accountStatus || "",
		employmentStatus: currentQuery?.employmentStatus || "",
		employmentType: currentQuery?.employmentType || "",
		service: currentQuery?.service || "",
		city: currentQuery?.city || "",
		gender: currentQuery?.gender || "",
		orderExist: currentQuery?.orderExist || "",
		contractStatus: currentQuery?.contractStatus || "",
		telegramExist: currentQuery?.telegramExist || "",
		scheduleExist: currentQuery?.scheduleExist || "",
		onlineStatus: currentQuery?.onlineStatus || "",
	});

	const updateQueryParams = useCallback(
		debounce((value) => {
			// reset the page: filtering shrinks the result set, so keeping the
			// current page can overflow the available range.
			const { fullUrl } = populateQueryParams(pageURL, {
				...value,
				page: null,
			});

			router.get(
				fullUrl,
				{},
				{
					preserveState: true,
					replace: true,
					only: ["therapists", "adminPortal"],
				},
			);
		}, 500),
		[],
	);

	const handleFilterBy = useCallback(
		({ value, type }: { value: string; type: keyof typeof filterBy }) => {
			const next = { ...filterBy, [type]: value };
			setFilterBy(next);
			updateQueryParams(deepTransformKeysToSnakeCase(next));
		},
		[filterBy, updateQueryParams],
	);

	const handleResetAllFilters = useCallback(() => {
		const reset = {
			search: "",
			accountStatus: "",
			employmentStatus: "",
			employmentType: "",
			service: "",
			city: "",
			gender: "",
			orderExist: "",
			contractStatus: "",
			telegramExist: "",
			scheduleExist: "",
			onlineStatus: "",
		};
		setFilterBy(reset);
		updateQueryParams(deepTransformKeysToSnakeCase(reset));
	}, [updateQueryParams]);

	// ── Active filter chips ───────────────────────────────────────────────
	const activeFilters = useMemo(() => {
		const result: {
			key: keyof typeof filterBy;
			label: string;
			value: string;
		}[] = [];
		if (filterBy.search) {
			result.push({
				key: "search",
				label: t("filter.label.search"),
				value: filterBy.search,
			});
		}
		if (filterBy.accountStatus) {
			result.push({
				key: "accountStatus",
				label: t("filter.label.account_status"),
				value:
					accountStatuses.find((s) => s.value === filterBy.accountStatus)
						?.label || filterBy.accountStatus,
			});
		}
		if (filterBy.employmentStatus) {
			result.push({
				key: "employmentStatus",
				label: t("filter.label.employment_status"),
				value: filterBy.employmentStatus,
			});
		}
		if (filterBy.employmentType) {
			result.push({
				key: "employmentType",
				label: t("filter.label.employment_type"),
				value: filterBy.employmentType,
			});
		}
		if (filterBy.service) {
			result.push({
				key: "service",
				label: t("filter.label.service"),
				// the query carries the id; show the brand name, falling back to the id so a
				// stale bookmarked id still renders as a removable chip
				value:
					services
						.find((s) => s.id === filterBy.service)
						?.name.replaceAll("_", " ") || filterBy.service,
			});
		}
		if (filterBy.city) {
			result.push({
				key: "city",
				label: t("filter.label.region"),
				value: filterBy.city,
			});
		}
		if (filterBy.gender) {
			result.push({
				key: "gender",
				label: t("filter.label.gender"),
				value:
					genderOptions.find((g) => g.value === filterBy.gender)?.label ||
					filterBy.gender,
			});
		}
		if (filterBy.orderExist) {
			result.push({
				key: "orderExist",
				label: t("filter.label.order_assigned"),
				value: t(`filter.options.exist.${filterBy.orderExist}`),
			});
		}
		if (filterBy.contractStatus) {
			result.push({
				key: "contractStatus",
				label: t("filter.label.contract_status"),
				value:
					contractStatusOptions.find((c) => c.value === filterBy.contractStatus)
						?.label || filterBy.contractStatus,
			});
		}
		if (filterBy.telegramExist) {
			result.push({
				key: "telegramExist",
				label: t("filter.label.telegram_linked"),
				value: t(`filter.options.exist.${filterBy.telegramExist}`),
			});
		}
		if (filterBy.scheduleExist) {
			result.push({
				key: "scheduleExist",
				label: t("filter.label.working_schedule"),
				value: t(`filter.options.exist.${filterBy.scheduleExist}`),
			});
		}
		if (filterBy.onlineStatus) {
			result.push({
				key: "onlineStatus",
				label: t("filter.label.activity"),
				value:
					onlineStatusOptions.find((o) => o.value === filterBy.onlineStatus)
						?.label || filterBy.onlineStatus,
			});
		}
		return result;
	}, [
		filterBy,
		accountStatuses,
		services,
		genderOptions,
		contractStatusOptions,
		onlineStatusOptions,
		t,
	]);

	return (
		<section className="space-y-4">
			<div className="flex items-center justify-between gap-2">
				<Button
					type="button"
					variant={preferences.isShowFilter ? "default" : "outline"}
					size={isMobile ? "icon" : "default"}
					className="shadow-none"
					onClick={(event) => {
						event.preventDefault();
						handleToggleFilter();
					}}
				>
					<ListFilterIcon className="size-4" />
					{isMobile
						? null
						: preferences.isShowFilter
							? t("filter.close")
							: t("filter.open")}
					{!preferences.isShowFilter && activeFilters.length > 0 && (
						<Badge variant="accent" className="px-1 rounded-full">
							{activeFilters.length}
						</Badge>
					)}
				</Button>

				<ColumnsMenu table={table} />
			</div>

			<AnimatePresence initial={false}>
				{preferences.isShowFilter && (
					<motion.div
						initial={{ opacity: 0, y: -20, filter: "blur(8px)" }}
						animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
						exit={{ opacity: 0, y: -20, filter: "blur(8px)" }}
						transition={{ duration: 0.3, ease: "easeOut" }}
						className="p-4 border shadow-inner rounded-xl bg-background"
					>
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
							<div className="col-span-full xl:col-span-2">
								<Input
									value={filterBy.search}
									StartIcon={{ icon: Search }}
									type="text"
									placeholder={t("filter.search_placeholder")}
									className="shadow-inner bg-input"
									onChange={(event) => {
										event.preventDefault();
										handleFilterBy({
											type: "search",
											value: event.target.value,
										});
									}}
								/>
							</div>

							<Select
								value={filterBy.accountStatus || ""}
								onValueChange={(value) => {
									handleFilterBy({ type: "accountStatus", value });
								}}
							>
								<SelectTrigger
									className={cn(
										"shadow-inner bg-input",
										!filterBy.accountStatus ? "text-muted-foreground" : "",
									)}
								>
									<SelectValue
										placeholder={t("filter.placeholder.account_status")}
									/>
								</SelectTrigger>

								<SelectContent>
									{accountStatuses.map((status) => (
										<SelectItem key={status.value} value={status.value}>
											<span className="flex items-center gap-2">
												<Dot
													className={status.color}
													width={10}
													height={10}
													strokeWidth={20}
												/>
												<span className="truncate">{status.label}</span>
											</span>
										</SelectItem>
									))}
									{filterBy.accountStatus && (
										<>
											<SelectSeparator />
											<Button
												className="w-full px-2 font-medium uppercase"
												variant="ghost"
												size="sm"
												onClick={(e) => {
													e.stopPropagation();
													handleFilterBy({ type: "accountStatus", value: "" });
												}}
											>
												{t("filter.clear")}
											</Button>
										</>
									)}
								</SelectContent>
							</Select>

							<Deferred
								data={["filterOptions"]}
								fallback={<Skeleton className="w-full rounded-md h-9" />}
							>
								<Select
									value={filterBy.employmentStatus || ""}
									onValueChange={(value) => {
										handleFilterBy({ type: "employmentStatus", value });
									}}
								>
									<SelectTrigger
										className={cn(
											"shadow-inner bg-input",
											!filterBy.employmentStatus ? "text-muted-foreground" : "",
										)}
									>
										<SelectValue
											placeholder={t("filter.placeholder.employment_status")}
										/>
									</SelectTrigger>

									<SelectContent>
										{employmentStatuses?.length ? (
											<>
												{employmentStatuses.map((status) => (
													<SelectItem key={status} value={status}>
														<span>{status}</span>
													</SelectItem>
												))}
												{filterBy.employmentStatus && (
													<>
														<SelectSeparator />
														<Button
															className="w-full px-2 font-medium uppercase"
															variant="ghost"
															size="sm"
															onClick={(e) => {
																e.stopPropagation();
																handleFilterBy({
																	type: "employmentStatus",
																	value: "",
																});
															}}
														>
															{t("filter.clear")}
														</Button>
													</>
												)}
											</>
										) : (
											<SelectItem value="no items" disabled>
												{t("filter.empty.employment_status")}
											</SelectItem>
										)}
									</SelectContent>
								</Select>
							</Deferred>

							<Deferred
								data={["filterOptions"]}
								fallback={<Skeleton className="w-full rounded-md h-9" />}
							>
								<Select
									value={filterBy.employmentType || ""}
									onValueChange={(value) => {
										handleFilterBy({ type: "employmentType", value });
									}}
								>
									<SelectTrigger
										className={cn(
											"shadow-inner bg-input",
											!filterBy.employmentType ? "text-muted-foreground" : "",
										)}
									>
										<SelectValue
											placeholder={t("filter.placeholder.employment_type")}
										/>
									</SelectTrigger>

									<SelectContent>
										{employmentTypes?.length ? (
											<>
												{employmentTypes.map((type) => (
													<SelectItem key={type} value={type}>
														<span>{type}</span>
													</SelectItem>
												))}
												{filterBy.employmentType && (
													<>
														<SelectSeparator />
														<Button
															className="w-full px-2 font-medium uppercase"
															variant="ghost"
															size="sm"
															onClick={(e) => {
																e.stopPropagation();
																handleFilterBy({
																	type: "employmentType",
																	value: "",
																});
															}}
														>
															{t("filter.clear")}
														</Button>
													</>
												)}
											</>
										) : (
											<SelectItem value="no items" disabled>
												{t("filter.empty.employment_type")}
											</SelectItem>
										)}
									</SelectContent>
								</Select>
							</Deferred>

							<Deferred
								data={["filterOptions"]}
								fallback={<Skeleton className="w-full rounded-md h-9" />}
							>
								<Select
									value={filterBy.service || ""}
									onValueChange={(value) => {
										handleFilterBy({ type: "service", value });
									}}
								>
									<SelectTrigger
										className={cn(
											"shadow-inner bg-input",
											!filterBy.service ? "text-muted-foreground" : "",
										)}
									>
										<SelectValue
											placeholder={t("filter.placeholder.service")}
										/>
									</SelectTrigger>

									<SelectContent>
										{services?.length ? (
											<>
												{services.map((service) => (
													<SelectItem key={service.id} value={service.id}>
														<span>{service.name.replaceAll("_", " ")}</span>
													</SelectItem>
												))}
												{filterBy.service && (
													<>
														<SelectSeparator />
														<Button
															className="w-full px-2 font-medium uppercase"
															variant="ghost"
															size="sm"
															onClick={(e) => {
																e.stopPropagation();
																handleFilterBy({ type: "service", value: "" });
															}}
														>
															{t("filter.clear")}
														</Button>
													</>
												)}
											</>
										) : (
											<SelectItem value="no items" disabled>
												{t("filter.empty.service")}
											</SelectItem>
										)}
									</SelectContent>
								</Select>
							</Deferred>

							<Deferred
								data={["filterOptions"]}
								fallback={<Skeleton className="w-full rounded-md h-9" />}
							>
								<Popover>
									<PopoverTrigger asChild>
										<Button
											variant="outline"
											className={cn(
												"relative w-full flex justify-between font-normal shadow-inner bg-input",
												!filterBy?.city && "text-muted-foreground",
											)}
										>
											<p className="truncate">
												{filterBy?.city
													? locations?.find(
															(location) => location.city === filterBy?.city,
														)?.city
													: t("filter.placeholder.region")}
											</p>
											<ChevronsUpDown className="opacity-50" />
										</Button>
									</PopoverTrigger>
									<PopoverContent
										className="p-0 w-[300px]"
										align="start"
										side="bottom"
									>
										<Command>
											<CommandInput
												placeholder={t("filter.search_region")}
												className="h-9"
												autoComplete="address-level2"
											/>
											<CommandList>
												<CommandEmpty>{t("filter.empty.region")}</CommandEmpty>
												{groupedLocations?.map((location) => (
													<Fragment key={location.country}>
														<span className="block px-2 py-2 text-xs font-bold text-primary-foreground bg-primary">
															{location.country}
														</span>

														{location.states.map((state, stateIndex) => (
															<CommandGroup
																key={state.name}
																heading={state.name}
															>
																{state.cities.map((city) => (
																	<CommandItem
																		key={city.name}
																		value={city.name}
																		onSelect={() =>
																			handleFilterBy({
																				type: "city",
																				value: city.name,
																			})
																		}
																	>
																		<span>{city.name}</span>
																		<Check
																			className={cn(
																				"ml-auto",
																				city.name === filterBy?.city
																					? "opacity-100"
																					: "opacity-0",
																			)}
																		/>
																	</CommandItem>
																))}
																{location.states.length !== stateIndex + 1 && (
																	<CommandSeparator className="mt-2" />
																)}
															</CommandGroup>
														))}
													</Fragment>
												))}
											</CommandList>

											<CommandList>
												<CommandGroup>
													<CommandSeparator className="mb-2" />
													<CommandItem
														onSelect={() => {
															handleFilterBy({ type: "city", value: "" });
														}}
													>
														<span className="mx-auto font-medium text-center uppercase w-fit">
															{t("filter.clear")}
														</span>
													</CommandItem>
												</CommandGroup>
											</CommandList>
										</Command>
									</PopoverContent>
								</Popover>
							</Deferred>

							<SelectFilter
								value={filterBy.gender}
								placeholder={t("filter.placeholder.gender")}
								options={genderOptions}
								clearLabel={t("filter.clear")}
								onValueChange={(value) =>
									handleFilterBy({ type: "gender", value })
								}
							/>

							<SelectFilter
								value={filterBy.onlineStatus}
								placeholder={t("filter.placeholder.activity")}
								options={onlineStatusOptions}
								clearLabel={t("filter.clear")}
								onValueChange={(value) =>
									handleFilterBy({ type: "onlineStatus", value })
								}
							/>

							<SelectFilter
								value={filterBy.orderExist}
								placeholder={t("filter.placeholder.order_assigned")}
								options={existOptions}
								clearLabel={t("filter.clear")}
								onValueChange={(value) =>
									handleFilterBy({ type: "orderExist", value })
								}
							/>

							<SelectFilter
								value={filterBy.contractStatus}
								placeholder={t("filter.placeholder.contract_status")}
								options={contractStatusOptions}
								clearLabel={t("filter.clear")}
								onValueChange={(value) =>
									handleFilterBy({ type: "contractStatus", value })
								}
							/>

							<SelectFilter
								value={filterBy.telegramExist}
								placeholder={t("filter.placeholder.telegram_linked")}
								options={existOptions}
								clearLabel={t("filter.clear")}
								onValueChange={(value) =>
									handleFilterBy({ type: "telegramExist", value })
								}
							/>

							<SelectFilter
								value={filterBy.scheduleExist}
								placeholder={t("filter.placeholder.working_schedule")}
								options={existOptions}
								clearLabel={t("filter.clear")}
								onValueChange={(value) =>
									handleFilterBy({ type: "scheduleExist", value })
								}
							/>
						</div>

						{activeFilters.length > 0 && (
							<div className="flex flex-wrap items-center gap-2 mt-6">
								<span className="text-xs font-medium text-muted-foreground">
									{t("filter.active")}
								</span>
								{activeFilters.map((f) => (
									<Badge
										key={f.key}
										variant="secondary"
										className="flex items-center gap-1 pl-2 pr-1 py-1 rounded-full text-[10px] uppercase tracking-wide"
									>
										<span>{f.label}:</span>
										<span className="font-bold">{f.value}</span>
										<button
											type="button"
											onClick={() => handleFilterBy({ type: f.key, value: "" })}
											className="inline-flex items-center justify-center rounded-full p-0.5 hover:bg-muted-foreground/15"
										>
											<X className="w-3 h-3" />
										</button>
									</Badge>
								))}

								<Button
									variant="destructive-outline"
									size="sm"
									className="rounded-full"
									onClick={handleResetAllFilters}
								>
									{t("filter.reset_all")}
								</Button>
							</div>
						)}

						<div className="flex flex-col justify-end gap-2 mt-4 md:flex-row">
							<Button
								variant="ghost"
								size="sm"
								onClick={handleToggleFilter}
								className="w-full md:w-fit"
							>
								{t("filter.close")}
							</Button>
							<Button
								variant="ghost-destructive"
								size="sm"
								onClick={handleResetAllFilters}
								className="w-full md:w-fit"
							>
								{t("filter.reset_all")}
							</Button>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</section>
	);
}

// Show/hide columns, as on the appointments page. Only columns the table renders and
// allows to hide are listed, so the mobile layout and the religion guard need no extra case.
function ColumnsMenu({ table }: ToolbarTableProps) {
	const { t } = useTranslation("therapists");
	const isMobile = useIsMobile();
	const columns = table.getAllLeafColumns().filter((c) => c.getCanHide());
	const hiddenCount = columns.filter((c) => !c.getIsVisible()).length;

	if (!columns.length) return null;

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					type="button"
					variant={hiddenCount ? "default" : "outline"}
					size={isMobile ? "icon" : "default"}
					className="shadow-none ml-auto"
				>
					<Columns3Icon className="size-4" />
					{isMobile ? null : t("button.columns.base")}
					{!!hiddenCount && (
						<Badge variant="accent" className="px-1 rounded-full">
							{columns.length - hiddenCount}/{columns.length}
						</Badge>
					)}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" side="bottom" className="w-fit">
				<DropdownMenuLabel>{t("button.columns.base")}</DropdownMenuLabel>
				<DropdownMenuSeparator />
				{columns.map((column) => (
					<DropdownMenuCheckboxItem
						key={column.id}
						checked={column.getIsVisible()}
						onSelect={(event) => {
							// keep the menu open so several columns can be switched in one go
							event.preventDefault();
							column.toggleVisibility();
						}}
					>
						{COLUMN_LABEL_KEYS[column.id]
							? t(COLUMN_LABEL_KEYS[column.id])
							: column.id}
					</DropdownMenuCheckboxItem>
				))}
				<DropdownMenuSeparator />
				<DropdownMenuItem
					disabled={!hiddenCount}
					onSelect={() => table.resetColumnVisibility(true)}
				>
					{t("button.columns.reset")}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
